/**
 * Danger-tile chance-event flow (HEX-81 origin; reframed in HEX-137 / ADR-0023).
 *
 * Stepping onto a `dangerous` tile (hidden until crossed, HEX-85) fires one
 * event from a weighted table (`events/eventTable.ts`), picked deterministically
 * from a seeded RNG the host derives from `(coords, crossings)`. Each event is a
 * short risk/reward beat:
 *
 *   - **Gamble** — wager gold on a d6: high (4–6) doubles the stake, low forfeits
 *     it. Double-or-nothing.
 *   - **Ambush** — a weak, below-band combat encounter, run through the shared
 *     card-combat overlay. Defeat routes the normal death pipe (DeathScreen →
 *     `defeat`); victory/forfeit carry the combat HP back and resume.
 *   - **Cache** — rolls gold / card / die (weighted, HEX-141). Gold credits
 *     directly; a card or die is offered beside the loadout via the shared
 *     acquisition drawer (HEX-149: place-or-decline, curate to make room).
 *   - **Quiet** — flavour, no effect.
 *
 * The card-sacrifice-to-disarm choice is dropped (ADR-0023): events are resolved,
 * not disarmed. DOM-overlay-above-Pixi, reusing the shared interaction-modal
 * chrome (same as ChestFlow / TownFlow). Character mutations persist on close.
 */

import { type CardCombatState, applyIntent, createCombat } from '../combat/cardEngine';
import type { DieType, Rng } from '../combat/dice';
import { rollPool } from '../combat/dice';
import { dieCard } from '../combat/diceRoster';
import { buildEnemy } from '../combat/enemies';
import type { EnemyTemplate } from '../combat/enemies';
import { makePlayerCombatant } from '../combat/player';
import { cardPrice } from '../combat/shopStock';
import type { Card } from '../combat/types';
import {
  CardCombatOverlay,
  type Encounter,
  type EngineDriver,
} from '../combat/ui/CardCombatOverlay';
import { DeathScreen } from '../combat/ui/DeathScreen';
import { type AcquisitionSession, openAcquisition } from '../combat/ui/curationDrawer';
import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import { dangerTierAt } from '../lifecycle/dangerEvent';
import { pickLatentEnemy } from '../lifecycle/frontierRisk';
import type { Persistence } from '../persistence/Persistence';
import {
  AMBUSH_DISTANCE,
  type ChanceEventDef,
  cacheGold,
  gambleStake,
  pickCacheCard,
  pickCacheDie,
  pickCacheReward,
  pickChanceEvent,
  pickQuietFlavour,
  resolveGamble,
} from './events/eventTable';

/** `survived` → host resumes the world; `defeat` → host exits to roster. */
export type EventOutcome = 'survived' | 'defeat';

export interface EventFlowDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter'>;
  /** ms-epoch source for the death stamp (host injects `Date.now`). */
  readonly now: () => number;
  /** Seeded RNG for the event roll (host seeds it from `eventSeed(tile, cross)`). */
  readonly rng: Rng;
  /**
   * The world generation seed (HEX-93). Folds frontier settlements into the
   * danger-tier metric so an event beside a frontier town reads at a lower tier.
   * Optional + additive — omit for the origin-only tier (pre-HEX-93 tests).
   */
  readonly worldSeed?: number;
}

export interface EventFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  readonly deps: EventFlowDeps;
  readonly onResolved: (outcome: EventOutcome) => void | Promise<void>;
}

export class EventFlow {
  private modal: HTMLDivElement | null = null;
  private deathScreen: DeathScreen | null = null;
  private acquiring: AcquisitionSession | null = null;
  private overlay: CardCombatOverlay | null = null;
  private combatState: CardCombatState | null = null;
  private active = false;

  constructor(private readonly container: HTMLElement) {}

  /**
   * Mount the event modal if the tile is `dangerous`. Returns true when mounted
   * (host sets its `activeOverlay` flag to pause world input); false otherwise.
   */
  start(opts: EventFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.tile.type !== 'dangerous') return false;
    this.active = true;
    const event = pickChanceEvent(opts.deps.rng());
    this.modal = this.mountModal(opts, event);
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.teardownModal();
    this.overlay?.destroy();
    this.overlay = null;
    this.combatState = null;
    this.deathScreen?.destroy();
    this.deathScreen = null;
    this.acquiring?.destroy();
    this.acquiring = null;
    this.active = false;
  }

  private mountModal(opts: EventFlowStartOptions, event: ChanceEventDef): HTMLDivElement {
    const root = document.createElement('div');
    root.className = 'hex6-interaction-modal';
    root.dataset.role = 'event-modal';
    root.dataset.event = event.kind;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', event.title);

    const panel = document.createElement('div');
    panel.className = 'hex6-interaction-modal__panel';

    const title = document.createElement('h2');
    title.className = 'hex6-interaction-modal__title';
    title.textContent = event.title;
    panel.appendChild(title);

    const body = document.createElement('div');
    body.className = 'hex6-interaction-modal__body';
    body.dataset.role = 'event-body';
    panel.appendChild(body);

    const actions = document.createElement('div');
    actions.className = 'hex6-interaction-modal__actions';
    actions.dataset.role = 'event-actions';
    panel.appendChild(actions);

    this.dispatch(opts, event, body, actions);

    root.appendChild(panel);
    this.container.appendChild(root);
    return root;
  }

  /** Route to the per-event opening view. */
  private dispatch(
    opts: EventFlowStartOptions,
    event: ChanceEventDef,
    body: HTMLElement,
    actions: HTMLElement,
  ): void {
    switch (event.kind) {
      case 'gamble':
        this.renderGamble(opts, body, actions);
        return;
      case 'ambush':
        this.renderAmbush(opts, body, actions);
        return;
      case 'cache':
        this.renderCache(opts, body, actions);
        return;
      case 'quiet':
        this.renderQuiet(opts, body, actions);
        return;
    }
  }

  // --- Gamble ---------------------------------------------------------------

  private renderGamble(opts: EventFlowStartOptions, body: HTMLElement, actions: HTMLElement): void {
    const stake = gambleStake(opts.character.gold);
    actions.replaceChildren();

    if (stake <= 0) {
      body.textContent =
        'A grinning stranger rattles a die at you — but you have not a coin to wager.';
      this.appendContinue(opts, actions, 'Move on');
      return;
    }

    body.textContent = `A grinning stranger offers a wager: stake ${stake} gold on one die. Roll high (4–6) and you double it; roll low and it is lost.`;

    const wager = document.createElement('button');
    wager.type = 'button';
    wager.dataset.action = 'event-gamble-wager';
    wager.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    wager.textContent = `Wager ${stake} gold`;
    wager.addEventListener('click', () => {
      this.doGamble(opts, body, actions, stake);
    });

    const walk = document.createElement('button');
    walk.type = 'button';
    walk.dataset.action = 'event-gamble-walk';
    walk.className = 'hex6-interaction-modal__button';
    walk.textContent = 'Walk away';
    walk.addEventListener('click', () => {
      void this.close(opts, 'survived');
    });

    actions.append(wager, walk);
  }

  private doGamble(
    opts: EventFlowStartOptions,
    body: HTMLElement,
    actions: HTMLElement,
    stake: number,
  ): void {
    const [face = 1] = rollPool(opts.deps.rng, 1);
    const result = resolveGamble(face, stake);
    const character = opts.character;
    character.gold = Math.max(0, character.gold + result.delta);
    const message = result.won
      ? `Rolled ${face} — you win! +${stake} gold. (${character.gold} gold)`
      : `Rolled ${face} — the die betrays you. −${stake} gold. (${character.gold} gold)`;
    this.renderResolved(opts, body, actions, message);
  }

  // --- Cache ----------------------------------------------------------------

  private renderCache(opts: EventFlowStartOptions, body: HTMLElement, actions: HTMLElement): void {
    const tier = dangerTierAt(opts.tile.q, opts.tile.r, opts.deps.worldSeed);
    // HEX-141: a cache rolls gold / card / die (weighted). The reward-kind draw
    // continues the event seed stream, so a given (coords, crossings) is
    // replayable. Card/die wiring is inline here, NOT a shared reward helper —
    // HEX-140 owns any such extraction.
    switch (pickCacheReward(opts.deps.rng())) {
      case 'card':
        this.renderCacheCard(opts, body, actions, tier);
        return;
      case 'die':
        this.renderCacheDie(opts, body, actions);
        return;
      default:
        this.renderCacheGold(opts, body, actions, tier);
    }
  }

  private renderCacheGold(
    opts: EventFlowStartOptions,
    body: HTMLElement,
    actions: HTMLElement,
    tier: number,
  ): void {
    const gold = cacheGold(tier);
    body.textContent = `Tucked beneath a loose stone you find a hidden cache: +${gold} gold.`;
    opts.character.gold += gold;
    actions.replaceChildren();
    this.appendContinue(opts, actions, 'Take it');
  }

  /** Cache die: announce, then on Take swap to the shared acquisition drawer
   * (HEX-149) so the die is offered beside the loadout — place it (1×1, ADR-0024
   * §2), curate to make room, or leave it. */
  private renderCacheDie(
    opts: EventFlowStartOptions,
    body: HTMLElement,
    actions: HTMLElement,
  ): void {
    const die = pickCacheDie(opts.deps.rng);
    body.textContent = `Tucked beneath a loose stone you find a hidden cache: a specialist die — the ${die.name}.`;
    actions.replaceChildren();

    const take = document.createElement('button');
    take.type = 'button';
    take.dataset.action = 'event-cache-take';
    take.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    take.textContent = 'Take it';
    take.addEventListener('click', () => {
      this.startCacheDiePrompt(opts, die);
    });
    actions.appendChild(take);
  }

  /** Swap the announce modal for the shared acquisition drawer with the die. */
  private startCacheDiePrompt(opts: EventFlowStartOptions, die: DieType): void {
    this.teardownModal();
    const session = openAcquisition(this.container, opts.character, {
      item: dieCard(die),
      declineLabel: 'Leave it',
    });
    this.acquiring = session;
    void session.done.then(() => {
      void this.close(opts, 'survived');
    });
  }

  /** Cache card: announce, then on Open swap to the shared acquisition drawer
   * (HEX-149) — mirrors the ambush announce→overlay swap so the event modal never
   * stacks under the drawer. */
  private renderCacheCard(
    opts: EventFlowStartOptions,
    body: HTMLElement,
    actions: HTMLElement,
    tier: number,
  ): void {
    const card = pickCacheCard(tier, opts.deps.rng);
    body.textContent = 'Tucked beneath a loose stone you find a hidden cache: a card lies within.';
    actions.replaceChildren();

    const open = document.createElement('button');
    open.type = 'button';
    open.dataset.action = 'event-cache-open';
    open.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    open.textContent = 'Open it';
    open.addEventListener('click', () => {
      this.startCacheCardPrompt(opts, card);
    });
    actions.appendChild(open);
  }

  /** Swap the announce modal for the shared acquisition drawer (HEX-149): the
   * card is offered beside the loadout — place it into a chosen cell, curate to
   * make room, or take its gold value (card-or-gold, HEX-72). */
  private startCacheCardPrompt(opts: EventFlowStartOptions, card: Card): void {
    this.teardownModal();
    const goldValue = cardPrice(card);
    const session = openAcquisition(this.container, opts.character, {
      item: card,
      declineLabel: `Take ${goldValue} gold`,
    });
    this.acquiring = session;
    void session.done.then((outcome) => {
      if (outcome === 'declined') opts.character.gold += goldValue;
      void this.close(opts, 'survived');
    });
  }

  // --- Quiet ----------------------------------------------------------------

  private renderQuiet(opts: EventFlowStartOptions, body: HTMLElement, actions: HTMLElement): void {
    body.textContent = pickQuietFlavour(opts.deps.rng());
    actions.replaceChildren();
    this.appendContinue(opts, actions, 'Continue');
  }

  // --- Ambush ---------------------------------------------------------------

  private renderAmbush(opts: EventFlowStartOptions, body: HTMLElement, actions: HTMLElement): void {
    const template = pickLatentEnemy(opts.deps.rng, AMBUSH_DISTANCE);
    body.textContent = `A ${template.name} springs from cover — there is no avoiding it. Stand and fight!`;
    actions.replaceChildren();

    const fight = document.createElement('button');
    fight.type = 'button';
    fight.dataset.action = 'event-ambush-fight';
    fight.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    fight.textContent = 'Fight!';
    fight.addEventListener('click', () => {
      this.startAmbushCombat(opts, template);
    });
    actions.appendChild(fight);
  }

  /** Swap the announce modal for the shared card-combat overlay (ADR-0015). */
  private startAmbushCombat(opts: EventFlowStartOptions, template: EnemyTemplate): void {
    this.teardownModal();

    const encounter: Encounter = {
      player: makePlayerCombatant(opts.character),
      enemy: buildEnemy(template),
      seed: (opts.deps.rng() * 0x1_0000_0000) >>> 0,
      enemyTier: template.tier,
      enemyGlyph: template.glyph,
    };

    const overlay = new CardCombatOverlay();
    const driver: EngineDriver = {
      createCombat: (args) => {
        const next = createCombat(args);
        this.combatState = next;
        return next;
      },
      applyIntent: (state, intent) => {
        const next = applyIntent(state, intent);
        this.combatState = next;
        return next;
      },
    };
    overlay.open(this.container, encounter, driver, (outcome) => {
      if (outcome === 'defeat') {
        this.ambushDeath(opts, template.name);
        return;
      }
      // victory or forfeit (Esc retreat) — both carry the combat HP back and
      // resume the world. A dangerous tile is never marked resolved: the event
      // re-rolls on a re-cross (seed folds `crossings`), as the trap did.
      void this.endAmbush(opts, template, outcome === 'victory');
    });
    this.overlay = overlay;
  }

  /** Tear down combat, write the survivor HP back (+ gold on victory), resume. */
  private async endAmbush(
    opts: EventFlowStartOptions,
    template: EnemyTemplate,
    victory: boolean,
  ): Promise<void> {
    const state = this.combatState;
    const character = opts.character;
    if (state !== null) {
      character.hp = Math.max(0, state.player.hp);
    }
    if (victory && template.drops.gold > 0) {
      character.gold += template.drops.gold;
    }
    this.overlay?.destroy();
    this.overlay = null;
    this.combatState = null;
    await this.close(opts, 'survived');
  }

  /** Defeat path: stamp `Character.death`, show the shared DeathScreen, exit. */
  private ambushDeath(opts: EventFlowStartOptions, enemyName: string): void {
    const { character, tile, deps } = opts;
    const hex = { q: tile.q, r: tile.r };
    character.hp = Math.max(0, this.combatState?.player.hp ?? 0);
    character.death = {
      at: deps.now(),
      hex,
      cause: enemyName,
      hp: character.hp,
    };

    this.overlay?.destroy();
    this.overlay = null;
    this.combatState = null;

    const screen = new DeathScreen(this.container);
    screen.start(
      {
        name: character.name,
        discoveries: character.discoveries,
        gold: character.gold,
        hp: character.hp,
        hex,
        cause: enemyName,
        inventory: character.inventory,
      },
      () => {
        void this.close(opts, 'defeat');
      },
    );
    this.deathScreen = screen;
  }

  // --- Shared chrome --------------------------------------------------------

  /** Terminal view with a single Continue action that resolves as survived. */
  private renderResolved(
    opts: EventFlowStartOptions,
    body: HTMLElement,
    actions: HTMLElement,
    message: string,
  ): void {
    body.textContent = message;
    actions.replaceChildren();
    this.appendContinue(opts, actions, 'Continue');
  }

  private appendContinue(opts: EventFlowStartOptions, actions: HTMLElement, label: string): void {
    const cont = document.createElement('button');
    cont.type = 'button';
    cont.dataset.action = 'event-continue';
    cont.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    cont.textContent = label;
    cont.addEventListener('click', () => {
      void this.close(opts, 'survived');
    });
    actions.appendChild(cont);
  }

  private teardownModal(): void {
    this.modal?.remove();
    this.modal = null;
  }

  private async close(opts: EventFlowStartOptions, outcome: EventOutcome): Promise<void> {
    if (!this.active) return;
    // Tear down before awaiting persistence so the UI stays responsive.
    this.teardownModal();
    this.overlay?.destroy();
    this.overlay = null;
    this.combatState = null;
    this.deathScreen?.destroy();
    this.deathScreen = null;
    this.acquiring?.destroy();
    this.acquiring = null;
    this.active = false;

    await opts.deps.persistence.saveCharacter(opts.character);
    await opts.onResolved(outcome);
  }
}
